import React, { useEffect, useRef, useState } from 'react';

import { ProductQuantityProps } from '../../components/product-quantity/product-quantity';
import Input from '../input/input';
import Typography from '../typography/typography';

import { isNumeric } from '../../shared/utils/validators/validators';

import './quantity.scss';

interface QuantityProps extends ProductQuantityProps {
  ariaLabel?: string;
  inputMode?: 'none' | 'numeric' | 'decimal';
  max: number;
  min: number;
  noValidate?: boolean;
  pattern?: string;
  unit?: string;
}

function Quantity(props: QuantityProps) {
  const {
    ariaLabel,
    className = '',
    defaultValue: defaultValueProp,
    id,
    inputMode = 'numeric',
    isDisabled = false,
    isReadOnly = false,
    isRequired = false,
    max,
    min,
    name,
    noValidate = true,
    onBlur,
    onChange,
    onFocus,
    pattern = '[0-9]*',
    showLabel,
    unit,
    value
  } = props;

  const { current: defaultValue } = useRef(defaultValueProp);
  const maxLength = String(max).length;
  const [inputValue, setInputValue] = useState(
    Number.isInteger(defaultValue) ? defaultValue : null
  );
  const [previousValue, setPreviousValue] = useState(inputValue);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let newValue;
    if (Number.isInteger(value)) {
      newValue = value;
    } else if (Number.isInteger(defaultValue)) {
      newValue = defaultValue;
    }

    setInputValue(newValue);
  }, [defaultValue, value]);

  function onKeyPress(e) {
    if (!isNumeric(e.key)) {
      e.preventDefault();
    }
  }

  function handleBlur(e) {
    const newValue = parseInt(e?.target?.value);

    let inputValue = newValue;

    if (Number.isInteger(max) && newValue > max) {
      inputValue = Number.isInteger(defaultValue) ? defaultValue : max;
    } else if (Number.isInteger(min) && newValue < min) {
      inputValue = Number.isInteger(defaultValue) ? defaultValue : min;
    } else if (isNaN(newValue) && Number.isInteger(previousValue)) {
      inputValue = Number(previousValue);
    }

    setInputValue(inputValue);
    setPreviousValue(inputValue);

    if (inputValue !== previousValue) {
      setMessage(`quantity has changed to ${inputValue}`);
      setTimeout(() => {
        setMessage('');
      }, 250);
    }

    if (onBlur) {
      onBlur(e, inputValue);
    }
  }

  function handleChange(e) {
    const value = e.target.value.slice(0, maxLength);
    const newValue = parseInt(value);

    setInputValue(newValue);

    if (onChange) {
      onChange(e, newValue);
    }
  }

  return (
    <div
      className={`quantity ${className}`}
      styleName={showLabel ? 'container-add-label' : 'container'}
    >
      {showLabel && (
        <Typography
          aria-hidden
          className="quantity__label"
          htmlFor={id || 'qty'}
          styleName="text"
          tag="label"
          variant="h6"
        >
          QTY
        </Typography>
      )}
      <Input {...props} className={className ? `${className}__input` : ''}>
        <Typography
          className="quantity__input-contents"
          styleName="input-contents"
          variant="h6"
        >
          <input
            aria-label={!showLabel ? ariaLabel : 'quantity-input'}
            className="quantity__input-field"
            data-testid={name ? `${name}-quantity` : 'quantity'}
            disabled={isDisabled}
            formNoValidate={noValidate}
            id={id || 'qty'}
            inputMode={inputMode}
            max={max}
            maxLength={maxLength}
            min={min}
            name={name}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={onFocus}
            onKeyPress={onKeyPress}
            pattern={pattern}
            readOnly={isReadOnly}
            required={isRequired}
            styleName={unit ? 'quantity-input-with-unit' : 'quantity-input'}
            type="number"
            value={
              Number.isInteger(inputValue) &&
              (!isNaN(Number(value)) || value === undefined)
                ? inputValue!.toString()
                : ''
            }
          />
          {unit && (
            <Typography
              className="quantity__unit"
              styleName="quantity-unit"
              tag="span"
              weight="heavy"
            >
              {unit}
            </Typography>
          )}
        </Typography>
      </Input>
      <Typography aria-live="polite" className="sr-only">
        {message}
      </Typography>
    </div>
  );
}

export default Quantity;
